import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

// Components
import GenericList from '~/components/GenericList/GenericList';
import GenericFilter from '~/components/GenericList/GenericFilter';
import Button from '../../../../components/CustomButton/CustomButton';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import { CurrentUserGroupsWidget } from '~/components/GenericForm/Widgets/UserGroupWidget';
import WorkPatternHistoryModal from '../WorkPatternHistoryModal';
import EditWorkPatternAssignmentsModal from './EditWorkPatternAssignments';
import { EmployeeCycleModal } from './ConflictCyclesModal';

//elements
import { DATE_FORMAT } from '../RosterScheduler';
import { work_pattern_assignment_statuses } from '~/utils/normalStatuses';
import { DEFAULT_VISIBILITY_WEEKS, AUTO_TIMESHEET_DAY_KEYS } from '~/utils/constants';

// Assets
import EditIcon from '~/assets/img/editing.svg';
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import { validateModuleAccess } from '../../../../library/stores/userStore';

//styles
import '../RosterScheduler/css/workPatternStyle.css';

class ManageAssignments extends Component {
    constructor(props) {
        super(props);
        this.state = {
            endDateModal: null,
            generatingId: null,
            pendingFilters: {},
            appliedFilters: {},
            showEditModal: false,
            selectedWorkPattern: null,
            workPatternLoadFn: null,
            editStartDate: null,
            visibilityWeeks: DEFAULT_VISIBILITY_WEEKS,
            currentEditEmployeeData: null,
            assignmentConflicts: [],
            showConflictView: false,
            isConflictLoading: false,
            workPatternDetails: null,
            isLoadingDetails: false,
            showHistoryModal: false,
            overrideComment: '',
            patternStartWeek: 1,
            editAutoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            editOverrideShift: false,
            editOverrideAutoTimeRecording: false,
            selectedEmployee: null,
        };
    }

    generate = (row) => {
        const { rosterStore, commonStore, t } = this.props;
        const dateFormat = commonStore.getDateTimeRules()?.short_date_format;
        const cycleLabel = row.next_cycle_start && row.next_cycle_end ? `${moment(row.next_cycle_start).format(dateFormat)} → ${moment(row.next_cycle_end).format(dateFormat)}` : null;

        confirmAlert({
            title: t('Generate Next Cycle'),
            message: (
                <>
                    <p className="fw-bold">{t('Generate next cycle for')} {row.employee_name}{cycleLabel ? ` - ${cycleLabel}` : ''}?</p>
                    <p className="text-muted fst-italic">{t('Note')}: {t('Any remaining days in the last cycle will be filled before the next cycle is generated.')}</p>
                </>
            ),
            buttons: [
                {
                    label: t('Generate'),
                    onClick: async () => {
                        this.setState({ generatingId: row.assignment_id });
                        try {
                            const res = await rosterStore.generateFromPattern({ assignment_id: row.assignment_id });
                            commonStore.addNotification(res?.message || t('Cycle generated successfully'), null, 'success');
                            rosterStore.refreshAssignmentList();
                        } catch (err) {
                            commonStore.addNotification(err?.message || t('Failed to generate cycle'), null, 'error');
                        } finally {
                            this.setState({ generatingId: null });
                        }
                    }
                },
                { label: t('Cancel'), onClick: () => { } }
            ]
        });
    };

    handleWorkPatternChange = async (option) => {
        if (!option) {
            this.setState({ selectedWorkPattern: null, workPatternDetails: null });
            return;
        }
        this.setState({ selectedWorkPattern: option, assignmentConflicts: [], showConflictView: false, workPatternDetails: null, isLoadingDetails: true });
        try {
            const details = await this.props.rosterStore.getWorkPatternSelectionDetails({ template_id: option.value });
            this.setState({ workPatternDetails: { ...option, ...details }, isLoadingDetails: false });
        } catch {
            this.setState({ isLoadingDetails: false });
        }
    }

    handleEditWorkPattern = async (emp) => {
        const { rosterStore, clientStore } = this.props;
        const workPatternLoadFn = (name, page) => rosterStore.getWorkPatterns({
            name, page, pagesize: 10,
            department: emp.department || '',
            exclude_id: emp.template_id,
        });
        this.setState({ workPatternLoadFn, showEditModal: true, currentEditEmployeeData: emp, patternStartWeek: emp.pattern_start_week || 1, editAutoTimesheetDays: emp.auto_timesheet_days || null, editOverrideShift: emp.override_existing_shift !== false, editOverrideAutoTimeRecording: emp.override_auto_time_recording !== false });
        if (emp.department) {
            try {
                const workHoursRule = await clientStore.getWorkHoursRuleByDepartment(emp.department);
                this.setState({ visibilityWeeks: workHoursRule?.work_pattern_visibility_weeks || DEFAULT_VISIBILITY_WEEKS });
            } catch {
                this.setState({ visibilityWeeks: DEFAULT_VISIBILITY_WEEKS });
            }
        }
    }

    saveEndDate = async () => {
        const { rosterStore, commonStore, t } = this.props;
        const { endDateModal } = this.state;
        try {
            const response = await rosterStore.updateAssignmentEndDate(endDateModal.empId, { end_date: endDateModal.value || null });
            commonStore.addNotification(response.message || t('Assignment end date updated successfully.'), null, 'success');
            this.setState({ endDateModal: null });
            rosterStore.refreshAssignmentList();
        } catch (err) {
            commonStore.addNotification(err?.message || t('Failed to update end date'), null, 'error');
        }
    };

    handleSaveEditWorkPattern = async () => {
        const { rosterStore, commonStore, t } = this.props;
        const dateTimeRule = commonStore.getDateTimeRules();
        const { selectedWorkPattern, editStartDate, currentEditEmployeeData, overrideComment } = this.state;

        if (!selectedWorkPattern || !editStartDate) {
            commonStore.addNotification(t('Please select a work pattern and start date'), null, 'error');
            return;
        }

        const isShortNotice = editStartDate && moment(editStartDate).isBefore(moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days').startOf('day'));
        if (isShortNotice && !overrideComment.trim()) {
            commonStore.addNotification(t('Please provide a reason for the short notice change.'), null, 'error');
            return;
        }

        this.setState({ isConflictLoading: true });
        try {
            const { conflict, assignmentConflict } = await rosterStore.validateAssignmentConflicts({
                userIds: [currentEditEmployeeData.user_id],
                assignmentStartDate: moment(editStartDate).format(DATE_FORMAT),
                currentAssignmentId: currentEditEmployeeData.assignment_id,
            });

            if (assignmentConflict && assignmentConflict.length > 0) {
                const c = assignmentConflict[0];
                const endLabel = c.end_date ? ` ${t('until')} ${moment(c.end_date).format(dateTimeRule.short_date_format)}` : '';
                commonStore.addNotification(`${t('Cannot reassign')}: ${currentEditEmployeeData.employee_name} ${t('already has an active assignment to')} "${c.template_name}"${endLabel}. ${t('Please end that assignment before changing.')}`, null, 'error');
                this.setState({ isConflictLoading: false });
                return;
            }

            if (conflict && conflict.length > 0) {
                this.setState({ assignmentConflicts: conflict, showConflictView: true, isConflictLoading: false });
            } else {
                this.setState({ showConflictView: false, assignmentConflicts: [], isConflictLoading: false });
                this.executeSave();
            }
        } catch (err) {
            commonStore.addNotification(err?.message || t('Failed to validate conflicts'), null, 'error');
            this.setState({ isConflictLoading: false });
        }
    }

    executeSave = async (isOverlap = false) => {
        const { rosterStore, commonStore, t } = this.props;
        const { selectedWorkPattern, editStartDate, currentEditEmployeeData, overrideComment, patternStartWeek, editAutoTimesheetDays, editOverrideShift, editOverrideAutoTimeRecording } = this.state;
        const isShortNotice = editStartDate && moment(editStartDate).isBefore(moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days').startOf('day'));
        try {
            const res = await rosterStore.updateUserAssignmentPatterns({
                userId: currentEditEmployeeData.user_id,
                currentAssignmentId: currentEditEmployeeData.assignment_id,
                newTemplateId: selectedWorkPattern.value,
                assignmentStartDate: moment(editStartDate).format(DATE_FORMAT),
                overlapConflicts: isOverlap,
                pattern_start_week: patternStartWeek || 1,
                overrideShift: editOverrideShift,
                overrideAutoTimeRecording: editOverrideAutoTimeRecording,
                ...(editAutoTimesheetDays ? { autoTimesheetDays: editAutoTimesheetDays } : {}),
                ...(isShortNotice && overrideComment && { override_comment: overrideComment }),
            });
            commonStore.addNotification(res?.message || t('Assignment updated successfully.'), null, 'success');
            this.setState({ showEditModal: false, currentEditEmployeeData: null, selectedWorkPattern: null, editStartDate: null, workPatternDetails: null, assignmentConflicts: [], showConflictView: false, overrideComment: '', workPatternLoadFn: null, patternStartWeek: 1 });
            rosterStore.refreshAssignmentList();
        } catch (err) {
            commonStore.addNotification(err?.message || t('Failed to update assignment'), null, 'error');
        }
    }

    renderExpirationCell = ({ assignment_id, end_date, start_date, employee_name, status }) => {
        const { commonStore, t } = this.props;
        const canManageWorkPattern = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');
        const dateTimeRule = commonStore.getDateTimeRules();
        const displayDate = end_date ? moment(end_date).format(dateTimeRule.short_date_format) : <i className="fas fa-infinity text-secondary"></i>;
        const isInactive = status === 'inactive';

        return (
            <div>
                <span className={`${end_date ? 'text-danger fw-600' : 'fst-italic'} me-1`}> {displayDate} </span>
                {canManageWorkPattern && (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`edit-exp-tooltip-${assignment_id}`}>{isInactive ? t('Only active assignments can be edited') : t('Edit Expiration Date')}</Tooltip>}
                    >
                        <span
                            className={isInactive ? 'cursor-not-allowed opacity-50 d-inline-block' : 'd-inline-block'}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button icon_sm nb disabled={isInactive} onClick={() => this.setState({ endDateModal: { empId: assignment_id, empName: employee_name, startDate: start_date, value: end_date || '' } })}>
                                <img src={EditIcon} alt="edit" style={{ width: '12px' }} />
                            </Button>
                        </span>
                    </OverlayTrigger>
                )}
            </div>
        );
    }

    renderActionsCell = (emp) => {
        const { t } = this.props;
        const { generatingId } = this.state;
        const canManageWorkPattern = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');

        const statusText = work_pattern_assignment_statuses.find(s => s.code === emp.status);
        const isInactive = emp.status === 'inactive';
        const isGenerateDisabled = generatingId === emp.assignment_id || isInactive || emp.automation;
        const isEditDisabled = isInactive || !emp.generated_cycles || !emp.generated_cycles.length;
        return (
            <div className="actions-center actions-center__right">
                <div className={`text-white status-block status-${emp.status === 'inactive' ? 'rejected' : emp.status || 'active'}`}>
                    {t(statusText ? statusText.name : emp.status)}
                </div>
                {canManageWorkPattern && (
                    <OverlayTrigger placement="top" overlay={
                        <Tooltip id={`generate-tooltip-${emp.assignment_id}`}>
                            {isInactive
                                ? t('Only active assignments can generate new cycles')
                                : emp.automation
                                    ? t('This work pattern generates cycles automatically')
                                    : t('Generate the next cycle for this work pattern assignment')}
                        </Tooltip>
                    }>
                        <span className={isGenerateDisabled ? 'cursor-not-allowed opacity-50 d-inline-block' : 'd-inline-block'}>
                            <Button icon_sm fill nb disabled={isGenerateDisabled} onClick={() => this.generate(emp)}>
                                {generatingId === emp.assignment_id ? <span className="spinner-border spinner-border-sm" role="status" /> : <i className="fa fa-refresh" />}
                            </Button>
                        </span>
                    </OverlayTrigger>
                )}
                {canManageWorkPattern && (
                    <OverlayTrigger key="work_pattern_edit" placement='top' overlay={
                        isEditDisabled && !isInactive
                            ? <></>
                            : (
                                <Tooltip id="tooltip-top">
                                    {isInactive ? t('Only active assignments can be edited') : `${t('Edit')} ${t('Work Pattern')} ${t('Assignments')}`}
                                </Tooltip>
                            )
                    }>
                        <span className={isEditDisabled ? 'opacity-50 d-inline-block cursor-not-allowed' : 'd-inline-block'}>
                            <Button icon_sm fill disabled={isEditDisabled} onClick={() => this.handleEditWorkPattern(emp)}><img src={EditIcon} alt="edit" /></Button>
                        </span>
                    </OverlayTrigger>
                )}
            </div>
        );
    }

    renderCycleSettingsInfoIcon = (emp) => {
        const { t, commonStore } = this.props;
        const clientData = commonStore?.config?.client?.data;
        const allowOverride = !!clientData?.basicRules?.workPlanSettings?.allow_optional_roster_shift_override;
        const allowShiftAutoTimesheetToggle = !!clientData?.loginRules?.allowShiftToEnableDisableAutoTimesheet;

        // Nothing configurable to report when both settings are off — skip the icon entirely.
        if (!allowOverride && !allowShiftAutoTimesheetToggle) return null;

        const overrideShift = emp.override_existing_shift !== false;
        const overrideAutoTimeRecording = overrideShift && emp.override_auto_time_recording !== false;
        const enabledDayLabels = AUTO_TIMESHEET_DAY_KEYS
            .filter(key => !!emp.auto_timesheet_days && emp.auto_timesheet_days[key] === true)
            .map(key => t(key.charAt(0).toUpperCase() + key.slice(1)));

        return (
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`cycle-settings-tooltip-${emp.assignment_id}`}>
                    <div className="text-start">
                        {allowOverride && (
                            <div><strong>{t('Override existing shifts')}</strong>: {overrideShift ? t('On') : t('Off')}</div>
                        )}
                        {allowOverride && allowShiftAutoTimesheetToggle && (
                            <div><strong>{t('Override auto time recording')}</strong>: {overrideAutoTimeRecording ? t('On') : t('Off')}</div>
                        )}
                        {allowShiftAutoTimesheetToggle && (
                            <div><strong>{t('Auto time recording')}</strong>: {enabledDayLabels.length ? enabledDayLabels.join(', ') : t('None')}</div>
                        )}
                    </div>
                </Tooltip>
            }>
                <i className="fa fa-info-circle text-muted flex-shrink-0 cursor-pointer" style={{ opacity: '0.6' }} aria-hidden="true"></i>
            </OverlayTrigger>
        );
    }

    renderGeneratedCyclesCell = (emp) => {
        const { generated_cycles = [] } = emp;
        const { t, commonStore } = this.props;

        if (!generated_cycles.length)
            return (
                <div className="d-flex align-items-center justify-content-center gap-1">
                    <span className="text-muted fst-italic">{t('Not generated')}</span>
                    {this.renderCycleSettingsInfoIcon(emp)}
                </div>
            );

        const dateFormat = commonStore.getDateTimeRules()?.short_date_format;
        const today = moment().startOf('day');

        const currentCycle = generated_cycles.find(c =>
            moment(c.start).isSameOrBefore(today, 'day') && moment(c.end).isSameOrAfter(today, 'day')
        );

        if (!currentCycle)
            return (
                <div className="d-flex align-items-center justify-content-center gap-1">
                    <span className="text-muted fst-italic">{t('No active cycle')}</span>
                    {this.renderCycleSettingsInfoIcon(emp)}
                </div>
            );

        return (
            <div className="d-flex flex-nowrap align-items-center justify-content-center gap-1">
                <span className="fw-600 text-nowrap text-primary-color small">
                    {moment(currentCycle.start).format(dateFormat)} &rarr; {moment(currentCycle.end).format(dateFormat)}
                </span>
                {currentCycle.is_merged && (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="shared-cycle-dot">
                        {t('This roster cycle covers multiple work patterns')}
                    </Tooltip>}>
                        <i className="fas fa-circle fa-xs text-primary-color flex-shrink-0 cursor-pointer" />
                    </OverlayTrigger>
                )}
                {this.renderCycleSettingsInfoIcon(emp)}
            </div>
        );
    }

    getColumns = () => {
        const { t, commonStore, router } = this.props;
        const dateFormat = commonStore.getDateTimeRules().short_date_format;
        const canManageWorkPattern = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');

        return [
            {
                Header: t('Employee'),
                id: 'employee_name',
                textAlign: 'start',
                accessor: (emp) => (
                    <span
                        className="text-start d-block cursor-pointer text-primary-color fw-600"
                        onClick={() => this.setState({ selectedEmployee: emp })}
                    >
                        {emp.employee_name}
                    </span>
                ),
                width: 120,
            },
            {
                Header: t('Template'),
                id: 'template_name',
                textAlign: 'start',
                accessor: ({ template_id, template_name }) => canManageWorkPattern ? (
                    <span
                        className="fw-600 text-start d-block text-primary-color cursor-pointer"
                        onClick={() => router.navigate('/admin/roster/work-pattern', { state: { editTemplateId: template_id } })}
                    >
                        {template_name}
                    </span>
                ) : (
                    <span className="fw-600 text-start d-block">{template_name}</span>
                ),
                width: 120,
            },
            {
                Header: t('Start') + ' ' + t('Week'),
                id: 'pattern_start_week',
                accessor: ({ pattern_start_week }) => (
                    <span className="text-muted">{pattern_start_week}</span>
                ),
                width: 80,
            },
            {
                Header: t('Start date'),
                id: 'start_date',
                accessor: ({ start_date }) => (
                    <span className="text-muted">{start_date ? moment(start_date).format(dateFormat) : '-'}</span>
                ),
                width: 80,
            },
            {
                Header: t('Cycle') + ' ' + t('Duration'),
                id: 'generated_cycles',
                accessor: this.renderGeneratedCyclesCell,
                width: 180,
            },
            {
                Header: t('Expiration'),
                id: 'end_date',
                accessor: this.renderExpirationCell,
                width: 150,
            },
            {
                Header: t('Created By'),
                id: 'created_by_name',
                accessor: ({ created_by_name }) => (
                    <span className="text-muted">{created_by_name || '-'}</span>
                ),
                width: 120,
            },
            {
                Header: `${t('Edited')} ${t('By')}`,
                id: 'edited_by_name',
                accessor: ({ edited_by_name }) => (
                    <span className="text-muted">{edited_by_name || '-'}</span>
                ),
                width: 120,
            },
            {
                Header: t('Actions'),
                id: 'actions',
                accessor: (emp) => this.renderActionsCell(emp),
                width: 200,
                textAlign: 'right',
            },
        ];
    };

    handleRequestData = (params) => {
        const { rosterStore, userStore } = this.props;
        let pmMembers = '';
        if (userStore?.currentUser?.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
            pmMembers = userStore.currentUser.Manages.join(',');
        }
        return rosterStore.getWorkPatternAssignments({
            ...params,
            search: params.filters?.name || '',
            status: params.filters?.status || '',
            department: params.filters?.department || '',
            user_group: params.filters?.user_group > 0 ? params.filters.user_group : '',
            pmMembers,
        });
    };

    handleUserGroupChange = (value) => {
        this.setState(prev => {
            const pendingFilters = { ...prev.pendingFilters, user_group: value };
            return { pendingFilters, appliedFilters: pendingFilters };
        });
    };

    render() {
        const { t, rosterStore, commonStore } = this.props;
        const { endDateModal, pendingFilters, appliedFilters, showEditModal, selectedWorkPattern, showHistoryModal, workPatternLoadFn } = this.state;
        const dateTimeRule = commonStore.getDateTimeRules();
        const roles = commonStore?.config?.client?.data?.roles || [];
        const departmentOptions = [
            { value: '', label: t('All') },
            ...roles.map(r => ({ value: r.code, label: r.name })),
            { value: 'z_none', label: t('Others') },
        ];

        return (
            <div className="pt-2">
                <GenericList
                    header={
                        <Row className="mb-0 align-items-start gx-2">
                            <Col md={2} className='pt-2 pe-3'>
                                <CurrentUserGroupsWidget
                                    value={pendingFilters.user_group || -1}
                                    onChange={this.handleUserGroupChange}
                                />
                            </Col>
                            <Col md={10} className="p-0">
                                <GenericFilter
                                    filters={pendingFilters}
                                    setFilter={(name, value) => this.setState(prev => ({ pendingFilters: { ...prev.pendingFilters, [name]: value } }))}
                                    onFilter={() => this.setState(prev => ({ appliedFilters: prev.pendingFilters }))}
                                    loading={false}
                                    hasSearch
                                    searchFilterPlaceholder={`${t('Search')} ${t('Employee')}`}
                                    hasStatusFilter
                                    statusOptions={work_pattern_assignment_statuses}
                                    hasCustomFilter
                                    customFilterName={{ value: 'department' }}
                                    customFilterOptions={departmentOptions}
                                />
                            </Col>
                        </Row>
                    }
                    columns={this.getColumns()}
                    requestData={this.handleRequestData}
                    lastListLoadTime={rosterStore.lastListLoadTime}
                    filters={appliedFilters}
                    pageSize={25}
                    t={t}
                />

                {endDateModal && (
                    <Modal show centered onHide={() => this.setState({ endDateModal: null })}>
                        <Modal.Header>
                            <Modal.Title className="fs14 fw-700">
                                {t('Edit')} {t('Expiration')} — {endDateModal.empName}
                            </Modal.Title>
                            <img
                                src={CloseModalIcon}
                                className="cursor-pointer"
                                alt='close icon'
                                onClick={() => this.setState({ endDateModal: null })}
                            />
                        </Modal.Header>
                        <Modal.Body>
                            <label className="fw-600 fs13 mb-2 d-block">{t('Expiration')} {t('Date')}</label>
                            <DatePicker
                                selected={endDateModal.value ? moment(endDateModal.value).toDate() : null}
                                onChange={date => this.setState({ endDateModal: { ...endDateModal, value: date ? moment(date).format(DATE_FORMAT) : '' } })}
                                minDate={endDateModal.startDate ? moment(endDateModal.startDate).add(1, 'day').toDate() : new Date()}
                                isClearable
                                popperProps={{ strategy: 'fixed' }}
                                popperPlacement="bottom-start"
                            />
                            <p className="text-muted fs13 mt-2 mb-0">{t('Leave empty to remove the expiration date.')}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => this.setState({ endDateModal: null })}>{t('Cancel')}</Button>
                            <Button fill onClick={this.saveEndDate}>{t('Save')}</Button>
                        </Modal.Footer>
                    </Modal>
                )}
                {
                    showEditModal && (
                        <EditWorkPatternAssignmentsModal
                            show={showEditModal}
                            state={this.state}
                            visibilityWeeks={this.state.visibilityWeeks}
                            onHide={() => this.setState({ showEditModal: false, currentEditEmployeeData: null, workPatternDetails: null, selectedWorkPattern: null, overrideComment: '', editStartDate: null, assignmentConflicts: [], showConflictView: false, showHistoryModal: false, workPatternLoadFn: null })}
                            t={t}
                            dateFormat={commonStore.getDateTimeRules()?.short_date_format}
                            workPatternLoadFn={workPatternLoadFn}
                            companyShiftMap={commonStore.companyShiftMap}
                            setState={(newState) => this.setState(newState)}
                            onWorkPatternChange={this.handleWorkPatternChange}
                            onSave={this.handleSaveEditWorkPattern}
                            onExecuteSave={this.executeSave}
                        />
                    )
                }

                <WorkPatternHistoryModal
                    show={showHistoryModal}
                    templateId={selectedWorkPattern?.value}
                    handleClose={() => this.setState({ showHistoryModal: false })}
                />

                <EmployeeCycleModal
                    show={!!this.state.selectedEmployee}
                    onHide={() => this.setState({ selectedEmployee: null })}
                    emp={this.state.selectedEmployee}
                    t={t}
                    dateFormat={dateTimeRule.short_date_format}
                />
            </div>
        );
    }
}

export default inject('rosterStore', 'commonStore', 'userStore', 'clientStore')(observer(ManageAssignments));
